import { FC, useState, useEffect, useMemo } from 'react';
import { format, addMonths, subMonths, startOfMonth, endOfMonth } from 'date-fns';
import { getLeaves, createLeave, updateLeave, deleteLeave, getStaffProfiles, updateStaffProfile } from '../../lib/scheduling';
import type { StaffLeave, LeaveType, LeaveStatus, StaffWithProfile } from '../../types/scheduling';
import { showToast } from '../../hooks/useToast';
import { useRole } from '../../hooks/useRole';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { createNotification } from '../../lib/notifications';

interface EnrichedStaffLeave extends StaffLeave {
  user_profiles?: {
    display_name: string;
  };
}

const LeaveManagementPage: FC = () => {
  const [leaves, setLeaves] = useState<EnrichedStaffLeave[]>([]);
  const [staff, setStaff] = useState<StaffWithProfile[]>([]);
  const [staffOptions, setStaffOptions] = useState<{ user_id: string; display_name: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  const [editingLeave, setEditingLeave] = useState<EnrichedStaffLeave | null>(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const { isAdmin } = useRole();
  const { user } = useAuth();
  const [activeFilter, setActiveFilter] = useState<LeaveStatus | 'all'>('all');
  const [formData, setFormData] = useState({
    user_id: '',
    leave_date: format(new Date(), 'yyyy-MM-dd'),
    leave_type: 'day_off' as LeaveType,
    reason: ''
  });

  // Revoke Modal State
  const [isRevoking, setIsRevoking] = useState(false);
  const [revokeReason, setRevokeReason] = useState('');
  const [leaveToRevoke, setLeaveToRevoke] = useState<EnrichedStaffLeave | null>(null);

  const isStaffUser = useMemo(() => !isAdmin && !!user, [isAdmin, user]);

  useEffect(() => {
    if (staffOptions.length === 0) return;

    if (isStaffUser && formData.user_id === '' && user) {
      setFormData(prev => ({ ...prev, user_id: user.id }));
    }
  }, [staffOptions, user, isStaffUser, formData.user_id]);

  useEffect(() => {
    loadData();
  }, [selectedMonth]);

  const fetchStaffUsers = async () => {
    const { data: staffRoles, error: rolesError } = await supabase
      .from('user_roles')
      .select('user_id')
      .eq('role', 'staff');

    if (rolesError) throw rolesError;
    if (!staffRoles || staffRoles.length === 0) return [];

    const staffUserIds = staffRoles.map(role => role.user_id);
    const { data: userProfiles, error: profilesError } = await supabase
      .from('user_profiles')
      .select('user_id, display_name')
      .in('user_id', staffUserIds);

    if (profilesError) throw profilesError;
    return userProfiles || [];
  };

  const fetchAdminUsers = async () => {
    const { data: adminRoles, error: rolesError } = await supabase
      .from('user_roles')
      .select('user_id')
      .eq('role', 'admin');

    if (rolesError) {
      console.error('Error fetching admins:', rolesError);
      return [];
    }
    return adminRoles?.map(role => role.user_id) || [];
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const startDate = format(startOfMonth(selectedMonth), 'yyyy-MM-dd');
      const endDate = format(endOfMonth(selectedMonth), 'yyyy-MM-dd');

      const [leavesData, staffData, staffUsersData] = await Promise.all([
        getLeaves(startDate, endDate, { status: 'all' }),
        getStaffProfiles(),
        fetchStaffUsers() // Always fetch all staff users
      ]);

      // Build user display name map for enriching leave data
      const userDisplayMap = new Map<string, string>();
      staffUsersData.forEach(user => {
        userDisplayMap.set(user.user_id, user.display_name || 'Unknown');
      });
      staffData.forEach(staffMember => {
        userDisplayMap.set(staffMember.user_id, staffMember.display_name);
      });

      // Enrich leaves with display names
      const enrichedLeaves = leavesData.map(leave => ({
        ...leave,
        user_profiles: { display_name: userDisplayMap.get(leave.user_id) || 'Unknown' }
      }));

      setLeaves(enrichedLeaves);
      setStaff(staffData);

      // Build staff options map
      const staffMap = new Map<string, { user_id: string; display_name: string }>();

      // First add all staff users from user_roles/user_profiles
      staffUsersData.forEach(user => {
        staffMap.set(user.user_id, {
          user_id: user.user_id,
          display_name: user.display_name || 'Unknown'
        });
      });

      // Then overlay with staff_profiles data (in case display name is more accurate)
      staffData.forEach(staffMember => {
        staffMap.set(staffMember.user_id, {
          user_id: staffMember.user_id,
          display_name: staffMember.display_name
        });
      });

      const finalStaffOptions = Array.from(staffMap.values()).sort((a, b) => a.display_name.localeCompare(b.display_name));
      console.log('Staff options loaded:', finalStaffOptions.length, finalStaffOptions);
      setStaffOptions(finalStaffOptions);
    } catch (error) {
      showToast('Failed to load leave data', 'error');
      console.error('Load data error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    console.log('Form submitted with data:', formData);

    try {
      if (editingLeave) {
        // Update existing leave
        const updates = {
          user_id: formData.user_id,
          leave_date: formData.leave_date,
          leave_type: formData.leave_type,
          reason: formData.reason
        };
        console.log('Updating leave:', editingLeave.id, updates);
        await updateLeave(editingLeave.id, updates);
        showToast('Leave request updated successfully', 'success');
      } else {
        // Create new leave - staff creates as pending, admin can create as approved
        const leaveData = {
          ...formData,
          status: (isAdmin ? 'approved' : 'pending') as LeaveStatus
        };
        console.log('Creating leave:', leaveData);
        const result = await createLeave(leaveData);
        console.log('Leave created:', result);
        showToast(`Leave request ${isAdmin ? 'created and approved' : 'submitted for approval'}`, 'success');
      }
      resetForm();
      await loadData();
    } catch (error: any) {
      console.error('Form submission error:', error);
      if (error.message?.includes('duplicate')) {
        showToast('Leave already exists for this date', 'error');
      } else {
        showToast(`Failed to ${editingLeave ? 'update' : 'create'} leave request`, 'error');
      }
    }
  };

  const handleUpdateStatus = async (id: string, status: LeaveStatus) => {
    try {
      await updateLeave(id, { status });

      // Status change notifications are handled by database triggers
      const statusMessage = status === 'approved' ? 'approved ✓' : status === 'rejected' ? 'rejected ✗' : status;
      showToast(`Leave ${statusMessage}`, status === 'approved' ? 'success' : status === 'rejected' ? 'error' : 'info');
      await loadData();
    } catch (error) {
      showToast('Failed to update leave status', 'error');
      console.error(error);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this leave request?')) {
      return;
    }

    try {
      await deleteLeave(id);
      showToast('Leave deleted successfully', 'success');
      await loadData();
    } catch (error) {
      showToast('Failed to delete leave', 'error');
      console.error(error);
    }
  };

  const handleRevokeClick = (leave: EnrichedStaffLeave) => {
    setLeaveToRevoke(leave);
    setRevokeReason('');
    setIsRevoking(true);
  };

  const confirmRevoke = async () => {
    if (!leaveToRevoke) return;

    try {
      const formattedDate = format(new Date(leaveToRevoke.leave_date), 'MMM dd, yyyy');

      // Update leave status
      await updateLeave(leaveToRevoke.id, {
        status: 'revoked',
        // Append revocation reason to existing reason
        reason: leaveToRevoke.reason ? `${leaveToRevoke.reason} [Revoked: ${revokeReason}]` : `[Revoked: ${revokeReason}]`
      });

      // Notify staff
      await createNotification(
        leaveToRevoke.user_id,
        'Leave Revoked',
        `Your leave on ${formattedDate} has been revoked due to ${revokeReason}.`,
        'warning',
        'leave',
        leaveToRevoke.id
      );

      // Notify all admins
      const adminIds = await fetchAdminUsers();
      await Promise.all(adminIds.map(adminId =>
        createNotification(
          adminId,
          'Leave Revoked',
          `Leave for ${leaveToRevoke.user_profiles?.display_name || 'staff'} on ${formattedDate} has been revoked due to ${revokeReason}.`,
          'info',
          'leave',
          leaveToRevoke.id
        )
      ));

      showToast('Leave revoked successfully', 'success');
      setIsRevoking(false);
      setLeaveToRevoke(null);
      await loadData();
    } catch (error) {
      showToast('Failed to revoke leave', 'error');
      console.error(error);
    }
  };

  const handleEditLeave = (leave: EnrichedStaffLeave) => {
    setEditingLeave(leave);
    setFormData({
      user_id: leave.user_id,
      leave_date: leave.leave_date,
      leave_type: leave.leave_type,
      reason: leave.reason || ''
    });
    setIsAdding(true);
  };

  const resetForm = () => {
    setFormData({
      user_id: '',
      leave_date: format(new Date(), 'yyyy-MM-dd'),
      leave_type: 'day_off',
      reason: ''
    });
    setIsAdding(false);
    setEditingLeave(null);
  };

  const handlePreviousMonth = () => {
    setSelectedMonth(subMonths(selectedMonth, 1));
  };

  const handleNextMonth = () => {
    setSelectedMonth(addMonths(selectedMonth, 1));
  };

  const getLeaveTypeColor = (type: LeaveType) => {
    switch (type) {
      case 'vacation': return 'bg-blue-100 text-blue-800';
      case 'sick': return 'bg-red-100 text-red-800';
      case 'day_off': return 'bg-green-100 text-green-800';
      case 'emergency': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status: LeaveStatus) => {
    switch (status) {
      case 'approved': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      case 'revoked': return 'bg-gray-100 text-gray-800 line-through';
    }
  };

  const filteredStaffOptions = useMemo(() => {
    if (isStaffUser) {
      return staffOptions.filter(option => option.user_id === user?.id);
    }
    return staffOptions;
  }, [isStaffUser, staffOptions, user?.id]);

  const filteredLeaves = useMemo(() => {
    if (activeFilter === 'all') return leaves;
    return leaves.filter(leave => leave.status === activeFilter);
  }, [leaves, activeFilter]);

  const approvedLeaveCounts = useMemo(() => {
    const counts = new Map<string, number>();
    leaves.forEach(leave => {
      if (leave.status === 'approved') {
        const current = counts.get(leave.user_id) || 0;
        counts.set(leave.user_id, current + 1);
      }
    });
    return counts;
  }, [leaves]);

  const statusFilters: { label: string; value: LeaveStatus | 'all' }[] = [
    { label: 'All', value: 'all' },
    { label: 'Pending', value: 'pending' },
    { label: 'Approved', value: 'approved' },
    { label: 'Rejected', value: 'rejected' },
    { label: 'Revoked', value: 'revoked' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl">Loading leaves...</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
      <div className="mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Leave Management</h1>
        <p className="text-gray-600">Manage staff leave requests and day-offs</p>
      </div>

      {/* Month Navigation */}
      <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 mb-4 sm:mb-6">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4">
          <button
            onClick={handlePreviousMonth}
            className="w-full sm:w-auto px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition"
          >
            ← Previous
          </button>

          <h2 className="text-lg sm:text-xl font-semibold">
            {format(selectedMonth, 'MMMM yyyy')}
          </h2>

          <button
            onClick={handleNextMonth}
            className="w-full sm:w-auto px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition"
          >
            Next →
          </button>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:gap-3 mt-4">
          {statusFilters.map(filter => (
            <button
              key={filter.value}
              onClick={() => setActiveFilter(filter.value)}
              className={`px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition ${activeFilter === filter.value
                ? 'bg-blue-600 text-white shadow'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
            >
              {filter.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mb-4 sm:mb-6">
        {!isAdding ? (
          <button
            onClick={() => setIsAdding(true)}
            className="w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium text-sm sm:text-base"
          >
            + Add Leave Request
          </button>
        ) : (
          <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4">
              {editingLeave ? 'Edit Leave Request' : 'Add Leave Request'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Staff Member *
                  </label>
                  <select
                    required
                    value={formData.user_id}
                    onChange={(e) => setFormData({ ...formData, user_id: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    disabled={isStaffUser}
                  >
                    <option value="">Select staff member</option>
                    {filteredStaffOptions.map((s) => (
                      <option key={s.user_id} value={s.user_id}>
                        {s.display_name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Leave Date *
                  </label>
                  <input
                    type="date"
                    required
                    value={formData.leave_date}
                    onChange={(e) => setFormData({ ...formData, leave_date: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Leave Type *
                  </label>
                  <select
                    value={formData.leave_type}
                    onChange={(e) => setFormData({ ...formData, leave_type: e.target.value as LeaveType })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="day_off">Day Off</option>
                    <option value="vacation">Vacation</option>
                    <option value="sick">Sick Leave</option>
                    <option value="emergency">Emergency</option>
                    <option value="other">Other</option>
                  </select>
                </div>

              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reason (Optional)
                </label>
                <textarea
                  value={formData.reason}
                  onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Optional reason for leave"
                />
              </div>

              <div className="flex flex-col sm:flex-row gap-3 pt-4">
                <button
                  type="submit"
                  className="w-full sm:w-auto px-4 sm:px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium"
                >
                  {editingLeave ? 'Update Leave Request' : 'Create Leave Request'}
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  className="w-full sm:w-auto px-4 sm:px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition font-medium"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}
      </div>

      {/* Leaves List */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="overflow-x-auto w-full">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Staff
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Reason
                </th>
                {isAdmin && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredLeaves.map((leave) => (
                <tr key={leave.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {leave.user_profiles?.display_name || 'Unknown'}
                      {approvedLeaveCounts.get(leave.user_id) ? ` (${approvedLeaveCounts.get(leave.user_id)})` : ''}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {format(new Date(leave.leave_date), 'MMM dd, yyyy')}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${getLeaveTypeColor(leave.leave_type)}`}>
                      {leave.leave_type.replace('_', ' ').toUpperCase()}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${getStatusColor(leave.status)}`}>
                      {leave.status.toUpperCase()}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-500 max-w-xs truncate">
                      {leave.reason || '-'}
                    </div>
                  </td>
                  {isAdmin && (
                    <td className="px-6 py-4 whitespace-nowrap text-sm space-x-2">
                      {leave.status === 'pending' && (
                        <>
                          <button
                            onClick={() => handleUpdateStatus(leave.id, 'approved')}
                            className="text-green-600 hover:text-green-800 font-medium"
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => handleUpdateStatus(leave.id, 'rejected')}
                            className="text-red-600 hover:text-red-800 font-medium"
                          >
                            Reject
                          </button>
                        </>
                      )}
                      {leave.status === 'approved' && (
                        <button
                          onClick={() => handleRevokeClick(leave)}
                          className="text-orange-600 hover:text-orange-800 font-medium"
                        >
                          Revoke
                        </button>
                      )}
                      <button
                        onClick={() => handleEditLeave(leave)}
                        className="text-blue-600 hover:text-blue-800 font-medium"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(leave.id)}
                        className="text-gray-600 hover:text-gray-800"
                      >
                        Delete
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredLeaves.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">No leave requests for this month.</p>
          </div>
        )}
      </div>

      {/* Revoke Confirmation Modal */}
      {
        isRevoking && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-bold mb-4">Revoke Approved Leave</h3>
              <p className="text-gray-600 mb-4">
                Please provide a reason for revoking this leave request.
              </p>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reason (Max 20 chars)
                </label>
                <input
                  type="text"
                  value={revokeReason}
                  onChange={(e) => setRevokeReason(e.target.value)}
                  maxLength={20}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g. Staff shortage"
                  autoFocus
                />
                <div className="text-right text-xs text-gray-500 mt-1">
                  {revokeReason.length}/20
                </div>
              </div>

              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setIsRevoking(false)}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmRevoke}
                  disabled={!revokeReason.trim()}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Confirm Revoke
                </button>
              </div>
            </div>
          </div>
        )
      }
    </div >
  );
};

export default LeaveManagementPage;
